"use client"

import React, { useState, useEffect, useMemo } from "react"
import TabContext from "@mui/lab/TabContext"
import TabList from "@mui/lab/TabList"
import TabPanel from "@mui/lab/TabPanel"
import Tab from "@mui/material/Tab"
import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/system"
import { Icons, CancelButton } from "@src/components"
import { useResponsive } from "@src/hooks"
import DrawerFilterMobile from "./drawer-filter-mobile"
import { useTranslation } from "react-i18next"

const StyledTab = styled(Tab)({
  fontWeight: 400,
  opacity: 0.5,
  color: "black !important",
  padding: "16px!important",
  margin: "0px",
  textTransform: "uppercase",
  "&.Mui-selected": {
    fontWeight: 700,
    opacity: 1
  }
})

const SubCategoryItem = styled(Box)(({ theme }) => ({
  padding: "0px 12px",
  opacity: 0.4,
  borderRadius: "32px",
  border: "1.5px solid #000000",
  height: "48px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&:hover": {
    opacity: 1
  },
  "&.active": {
    opacity: 1,
    "& p": {
      fontWeight: 700
    }
  },
  [theme.breakpoints.down("md")]: {
    height: "40px"
  }
}))

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`
  }
}

export const SubCategory = ({ name, id, active, onSelected }) => {
  return (
    <Stack
      display="inline-block"
      sx={{
        margin: "0px 8px 8px 0px"
      }}
      onClick={onSelected}
    >
      <SubCategoryItem className={active && "active"}>
        <Typography variant="body1">{name}</Typography>
      </SubCategoryItem>
    </Stack>
  )
}

export const OPTION_ALL = {
  id: -1,
  name: 'All',
  key: "all",
  slug: "all"
}

const TabCategory = props => {
  const { t } = useTranslation()
  const { isMobile } = useResponsive()
  const {
    categories: entries,
    currentCategoryId = -1,
    currentSubCategorySlug,
    onSelectCategory,
    onSelectSubCategory,
    onSelectCatAndSub
  } = props
  const OPTION_ALL = {
    id: -1,
    name: t("all"),
    key: "all",
    slug: "all"
  }

  const [openFilterDrawer, setOpenFilterDrawer] = useState(false)

  const handleChangeTab = (_, newValue) => {
    const catSlug = entries.find(item => item.id === newValue)?.slug || "all"
    onSelectCategory(catSlug)
  }

  const handleToggleFilterDrawer = status => () => {
    setOpenFilterDrawer(status ?? !openFilterDrawer)
  }

  const categories = useMemo(() => {
    return [OPTION_ALL, ...entries]
  }, [entries])

  const subCategory = useMemo(() => {
    if (currentCategoryId === -1 || !currentCategoryId) {
      return null
    }
    return entries.find(item => item.id === currentCategoryId)?.categories || []
  }, [currentCategoryId])

  const renderSubCategory = () => {
    if (subCategory) {
      if (isMobile) {
        return (
          <Stack gap={2} alignItems="center">
            <Icons name="filter" />
            <CancelButton
              label="Lọc"
              minHeight="40px"
              minWidth="80px"
              endIcon={<Icons name="arrow-down" />}
              onClick={handleToggleFilterDrawer(true)}
            />
          </Stack>
        )
      }
      return [OPTION_ALL, ...subCategory].map(item => (
        <SubCategory
          key={item.id}
          id={item.id}
          name={item.name}
          active={currentSubCategorySlug?.includes(item.slug)}
          onSelected={() =>
            onSelectSubCategory({
              id: item.id,
              slug: item.slug
            })
          }
        />
      ))
    }
    return null
  }

  return (
    <>
      <TabContext value={currentCategoryId}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "#0000002e"
          }}
        >
          <TabList
            sx={{
              "& .MuiTabs-indicator": {
                backgroundColor: "black"
              },
              "& .MuiSvgIcon-root": {
                fontSize: "24px"
              },
              "& .MuiTabs-scrollButtons.Mui-disabled": {
                opacity: 0.3
              },
              "& .MuiTabScrollButton-root": {
                width: "20px",
                height: "20px",
                backgroundColor: "#7D7D7D",
                borderRadius: "50%",
                margin: "auto 2px",
                "& svg": {
                  color: "white",
                  fontSize: "18px"
                }
              },
              "& .MuiTabs-flexContainer": {
                justifyContent: "space-between"
              }
            }}
            className="tab-category"
            variant="scrollable"
            aria-label="scrollable auto tabs example"
            scrollButtons="auto"
            allowScrollButtonsMobile
            onChange={handleChangeTab}
          >
            {categories.map((item, index) => (
              <StyledTab
                key={index}
                label={item.name}
                value={item.id}
                {...a11yProps(index)}
                sx={{
                  fontSize: {
                    xs: 14,
                    md: 16
                  },
                  minWidth: {
                    xs: "80px",
                    md: "100px"
                  }
                }}
              />
            ))}
          </TabList>
        </Box>
        {categories.map((item, index) => (
          <TabPanel
            sx={{
              paddingBottom: 0,
              paddingTop: 2
            }}
            key={index}
            value={item.id}
          >
            {renderSubCategory()}
          </TabPanel>
        ))}
      </TabContext>
      <DrawerFilterMobile
        open={openFilterDrawer}
        currentCategoryId={currentCategoryId}
        currentSubCategorySlug={currentSubCategorySlug}
        categories={entries}
        toggleDrawer={handleToggleFilterDrawer}
        onSubmitCategory={onSelectCatAndSub}
      />
    </>
  )
}

export default TabCategory
